// web/src/api.ts — typed fetch client to the bg-api. OWNED BY S4 (S4 adds the
// per-endpoint typed wrappers). Base URL from VITE_API_BASE; defaults to same
// origin (Vite dev proxies /api -> wrangler dev).

import type {
  AdmitInput,
  AuditEntry,
  DeliverShardInput,
  EnrollmentStatus,
  InviteGuestInput,
  KeyAlg,
  KeyKind,
  PendingProof,
  Principal,
  PublishKeyInput,
  RecordDetail,
  RecordSummary,
  RegisteredKey,
  ResponsibilityView,
  ShardDelivery,
  SealReceipt,
  SealRecordInput,
  SecretRecord,
  SessionMember,
  SubmitShardInput,
  UnsealJoinContext,
  UnsealRequest,
  UnsealStatus,
  VerifyMagicLinkInput,
} from '../../shared/types';
import { ACT_AS_HEADER, getActAs } from './auth';

// API origin. Defaults to the deployed bg-api; dev sets VITE_API_BASE='' so the
// Vite proxy forwards /records, /quorum, /enroll, … to local `wrangler dev`.
const API_BASE: string =
  import.meta.env.VITE_API_BASE ?? 'https://api-bg.planesailing.io';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Generic JSON request helper. S4 builds typed wrappers on top of this. */
export async function request<T>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      [ACT_AS_HEADER]: getActAs(),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new ApiError(res.status, text || res.statusText);
  }
  return (await res.json()) as T;
}

// --- Typed wrappers (signatures only; TODO(S4) implement bodies as needed) --
export const api = {
  me: () => request<Principal>('/me'),

  // NOTE: the legacy single-approver surfaces (/secrets, /requests, /grants) are
  // RETIRED server-side (ADR-003 D2 / C1) — their tables are dropped in migration
  // 0003 and their routers/wrappers deleted. The live write-path is `sealRecord`
  // (POST /records), which seals CLIENT-SIDE; reveal is now the client-side unseal
  // ceremony (/quorum). No wrapper here references a removed route.

  listAudit: () => request<AuditEntry[]>('/audit'),
  verifyAudit: () =>
    request<{ ok: boolean; count: number; brokenAtSeq: number | null }>('/audit/verify'),

  // --- Enrollment & sealing write-path (ADR-003 / M2) ----------------------
  //
  // The server receives CIPHERTEXT + METADATA ONLY. None of these wrappers ever
  // carry plaintext, a data key, or a private key (R11) — `sealRecord` posts only
  // the opaque Envelope params + per-member WireShard ciphertext.

  /** Open an enrollment session (initiator). Returns the one-time join code + PIN. */
  createEnrollSession: () =>
    request<EnrollSessionReceipt>('/enroll', { method: 'POST', body: '{}' }),

  /**
   * Fetch a session's public state (status, quorum context). The path param
   * resolves by SESSION ID (initiator landing) OR by JOIN CODE (what3words) for
   * the guest/receiver magic-link landing (C4): a guest arriving at
   * `…/join/<code>?ml=<token>` calls this with `<code>` to learn the real session
   * id, then consumes the token via `verifyMagicLink(session.id, …)`.
   */
  getEnrollSession: (sessionIdOrCode: string) =>
    request<EnrollSessionPublic>(`/enroll/${encodeURIComponent(sessionIdOrCode)}`),

  /** Poll the live lobby roster — joiners + their admit state (initiator). */
  sessionRoster: (sessionId: string) =>
    request<SessionMember[]>(`/enroll/${encodeURIComponent(sessionId)}/roster`),

  /**
   * Enter the lobby for a join code (receiver). Resolves the code → its open
   * session and links the caller's published pubkey as a JOINED member. The code
   * grants LOBBY ENTRY ONLY (ADR-003 D3); admit is a separate human decision.
   */
  joinSession: (input: JoinSessionInput) =>
    request<JoinSessionReceipt>('/enroll/join', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Admit a joiner after an out-of-band safety-number check (initiator-only). */
  admitJoiner: (sessionId: string, input: AdmitInput) =>
    request<SessionMember>(`/enroll/${sessionId}/admit`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Reject a joiner; the slot is freed and audited (initiator-only). */
  rejectJoiner: (sessionId: string, input: AdmitInput) =>
    request<SessionMember>(`/enroll/${sessionId}/reject`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Mint a single-use, TTL'd, session-bound guest magic link (initiator-only). */
  inviteGuest: (sessionId: string, input: InviteGuestInput) =>
    request<{ sessionId: string; email: string; expiresAt: number }>(
      `/enroll/${sessionId}/invite`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  /** Consume a magic-link token → a session-scoped guest principal (receiver). */
  verifyMagicLink: (sessionId: string, input: VerifyMagicLinkInput) =>
    request<GuestPrincipal>(`/enroll/${sessionId}/verify`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Publish a PUBLIC key to the registry; the Worker binds + attests it (receiver). */
  publishKey: (input: PublishKeyInput) =>
    request<RegisteredKey>('/registry/keys', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Fetch one registered key (+ attestation + safety number) by its pubkeyId. */
  getKey: (pubkeyId: string) =>
    request<RegisteredKey>(`/registry/keys/${encodeURIComponent(pubkeyId)}`),

  /** Seal: the SINGLE write-path. Posts the envelope meta + per-member ciphertext. */
  sealRecord: (input: SealRecordInput) =>
    request<SealReceipt>('/records', {
      method: 'POST',
      // Bind idempotency to the session so a retried POST does not double-write.
      headers: { 'Idempotency-Key': input.sessionId },
      body: JSON.stringify(input),
    }),

  /**
   * Open a break-glass unseal ceremony for a sealed record. `requesterEphemeralJwk`
   * is the requester's EPHEMERAL ECDH PUBLIC key (JSON) — holders re-encrypt their
   * decrypted share TO it before delivery (M3.2), so the server only ever stores
   * an opaque blob it cannot open. The matching private key never leaves the browser.
   */
  requestUnseal: (
    secretId: string,
    requesterPubkeyId: string,
    justification: string,
    requesterEphemeralJwk?: string,
  ) =>
    request<UnsealRequest & { pin: string }>(
      `/quorum/${encodeURIComponent(secretId)}/request`,
      {
        method: 'POST',
        body: JSON.stringify({ requesterPubkeyId, justification, requesterEphemeralJwk }),
      },
    ),

  /**
   * Resolve an OPEN unseal ceremony by its join code + PIN (mirror enrollment). On
   * success returns the ceremony context the holder needs to connect; the holder
   * then fetches their own shard via `myShard` and submits as today. The PIN is
   * gated by a per-code attempt budget; an expired/closed ceremony is 410 (R11).
   */
  joinUnseal: (joinWords: string, pin: string) =>
    request<UnsealJoinContext>('/quorum/join', {
      method: 'POST',
      body: JSON.stringify({ joinWords, pin }),
    }),

  /**
   * Deliver an OPAQUE encrypted share over the store-and-forward relay (M3.2).
   * `blob` is the holder's decrypted share re-encrypted to the requester's
   * ephemeral public key — the server stores/relays it but cannot read it.
   */
  deliverShard: (requestId: string, input: DeliverShardInput) =>
    request<{ delivered: boolean; memberId: string; shareIndex: number }>(
      `/quorum/${encodeURIComponent(requestId)}/deliver`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  /**
   * Drain the OPAQUE delivered blobs for the caller's OWN ceremony (M3.2). The
   * server returns them only to the requester (403 otherwise); each blob is
   * decrypted in-browser with the ephemeral private key, never server-side (R11).
   */
  unsealDeliveries: (requestId: string) =>
    request<{ requestId: string; deliveries: ShardDelivery[] }>(
      `/quorum/${encodeURIComponent(requestId)}/deliveries`,
    ),

  /**
   * Relay an OPAQUE WebRTC signaling frame peer→peer over the DO (M3.2 SECONDARY
   * path). The payload is opaque SDP/ICE — it negotiates a direct DataChannel and
   * carries no share or key. Best-effort; failure falls back to the relay above.
   */
  relaySignal: (
    requestId: string,
    input: { kind: 'offer' | 'answer' | 'ice'; to: string; payload: string },
  ) =>
    request<{ relayed: boolean }>(`/quorum/${encodeURIComponent(requestId)}/signal`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  /** Record a shard arrival (ciphertext-free metadata) for an unseal ceremony. */
  submitShard: (requestId: string, input: SubmitShardInput) =>
    request<{ submission: { submittedCount: number; thresholdK: number }; status?: UnsealStatus }>(
      `/quorum/${encodeURIComponent(requestId)}/submit`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  /** Ceremony progress (the safe's bolt count): counts + threshold + status. */
  unsealStatus: (requestId: string) =>
    request<{
      requestId: string;
      secretId: string;
      status: UnsealStatus;
      submittedCount: number;
      thresholdK: number;
      expiresAt: number;
      /** The requester's ephemeral PUBLIC key (JSON); holders encrypt their share TO it. */
      requesterEphemeralJwk: string | null;
    }>(`/quorum/${encodeURIComponent(requestId)}/status`),

  /** Answer a possession-proof challenge with an ECDSA signature over the nonce. */
  answerPossession: (proofId: string, signature: string) =>
    request<{ status: string; verifiedAt: number | null }>(
      `/registry/possession/${encodeURIComponent(proofId)}/answer`,
      { method: 'POST', body: JSON.stringify({ signature }) },
    ),

  /**
   * The caller's pending-proof inbox (M2.7): outstanding possession-proof
   * challenges across every record the caller holds a non-revoked shard in. The
   * server lazily mints a due challenge per held slot whose cadence has elapsed.
   * Each item carries a PUBLIC nonce to sign via `answerPossession` (R11).
   */
  pendingProofs: () => request<PendingProof[]>('/registry/possession/mine'),

  // --- Records read-path + responsibility/reporting (M4.2/M4.3/M4.7/M4.8; R15) --
  //
  // METADATA ONLY. These wrappers return ids, counts, statuses, public keys/
  // fingerprints and timestamps — never plaintext, a key, or a shard ciphertext.

  /** List records the caller owns or holds a shard in (status, K/N, quorum health). */
  listRecords: () => request<RecordSummary[]>('/records'),

  /** Record detail: members + fingerprints + threshold + ceremony history + owner. */
  getRecord: (id: string) =>
    request<RecordDetail>(`/records/${encodeURIComponent(id)}`),

  /**
   * The audit slice for ONE record (custody-owner-only). Returns that record's
   * audit entries; gated server-side to the record's OWNER (403 otherwise). This
   * is the real per-record audit surface — the global feed is admin-only (R11).
   */
  recordAudit: (id: string) =>
    request<AuditEntry[]>(`/records/${encodeURIComponent(id)}/audit`),

  /**
   * Set the owner-defined possession-proof cadence for a record (M2.7). Seconds;
   * must be a positive integer. Owner/admin-only. Pure policy metadata — no
   * material is touched. Returns the updated record.
   */
  setProofInterval: (id: string, proofIntervalSec: number) =>
    request<SecretRecord>(`/records/${encodeURIComponent(id)}/proof-interval`, {
      method: 'POST',
      body: JSON.stringify({ proofIntervalSec }),
    }),

  /**
   * Fetch the CALLER'S OWN encrypted WireShard for a record (ADR-003 D8). This is
   * legitimate self-delivery: the ciphertext is sealed to the caller's PUBLIC key,
   * so only they can decrypt it locally — the server relays opaque bytes it cannot
   * open and NEVER returns another member's shard (403 to a non-holder). The
   * response carries metadata + the caller's own ciphertext only (no plaintext).
   */
  myShard: (id: string) =>
    request<MyShard>(`/records/${encodeURIComponent(id)}/my-shard`),

  /** Records the caller is the responsible party for: last proof + ceremonies + roster. */
  listResponsibilities: () =>
    request<ResponsibilityView[]>('/responsibilities'),

  /**
   * Transfer ownership of a record to another member (R15 / M4.8). METADATA +
   * AUDIT ONLY: shards are NOT re-encrypted or moved; the server flips owner_id
   * and emits an `ownership.transferred` audit event.
   */
  transferOwnership: (secretId: string, toOwnerId: string) =>
    request<SecretRecord>(
      `/quorum/secret/${encodeURIComponent(secretId)}/transfer`,
      { method: 'POST', body: JSON.stringify({ toOwnerId }) },
    ),
};

// --- Enrollment / sealing wrapper response shapes -----------------------------

/** One-time receipt returned by POST /enroll (the PIN is served exactly once). */
export interface EnrollSessionReceipt {
  id: string;
  status: EnrollmentStatus;
  joinWords: string;
  pin: string;
  expiresAt: number;
}

/** Session-scoped guest principal yielded by consuming a magic-link token. */
export interface GuestPrincipal {
  sessionId: string;
  email: string;
  principalId: string;
  authMethod: 'magic-link';
}

/** Public state of a session (no PIN — that is served once at create time). */
export interface EnrollSessionPublic {
  id: string;
  status: EnrollmentStatus;
  joinWords: string;
  expiresAt: number;
}

/** Enter a lobby by join code (receiver). Carries the freshly-published pubkey. */
export interface JoinSessionInput {
  joinWords: string;
  pin: string;
  pubkeyId: string;
  /** Present only on the magic-link guest path (consumed via verifyMagicLink). */
  guestPrincipalId?: string;
}

/** Receipt of entering the lobby — the member's JOINED record + session id. */
export interface JoinSessionReceipt {
  sessionId: string;
  member: SessionMember;
}

/**
 * The caller's own opaque shard for a record (ADR-003 D8 store-and-forward). The
 * ciphertext is sealed to the caller's PUBLIC key; `shardCiphertext` is null in
 * transient/WebRTC mode (no stored blob to relay). Metadata + own ciphertext only.
 */
export interface MyShard {
  secretId: string;
  memberId: string;
  shareIndex: number;
  shardCiphertext: string | null;
}

/** Registry publish alg/kind defaults for the in-browser ECDH decryption key. */
export const DECRYPT_KEY_ALG: KeyAlg = 'ECDH-ES';
export const SOFTWARE_KEY_KIND: KeyKind = 'software';
